/**
 * Local-first mutations for the course domain (Aula).
 *
 * They write to the local PowerSync DB (Drizzle) → the sync queue sends them to
 * the server (`db/sync.rs`), which applies authz (author/editor, CC-40), seals the
 * course version and RE-CORRECTS progress (anti-fraud). The client is never
 * the authority: the final grade/XP comes back through sync.
 *
 * ── §4.14 · Why NONE of these mutations carry `options.scope` ────────────────
 *
 * The canon (`comandero-app`, `f8f1049`) extends `patchRowVerified` with
 * `options.scope`: extra equality conditions — typically
 * `organization_id` — applied to the UPDATE and the re-read, because
 * `infra/sync_streams.yaml` brings the client ALL the organizations it is
 * `member` of (not only the active one) and a foreign `id` can arrive via URL or
 * via sync. Explicitly measured for this repo (PENDING_WORK §4.14): the
 * 9 non-dead mutations of `course`/`unit`/`lesson`/`block` (above and
 * below) CAN receive locally rows from more than one organization — via the
 * `org_courses` streams (member of the publishing org), `catalog`
 * (public), `my_authored_courses` and `my_editor_courses` at once, if the
 * user belongs to several academies. But **there is no `organization_id` to
 * narrow by**:
 *
 * 1. None of the 6 domain tables (`course`, `unit`, `lesson`,
 *    `block`, `enrollment`, `lesson_progress`) has that column — the
 *    relation to an organization goes through the bridge table
 *    `course_organization`, and it is MANY-TO-MANY: the same course can
 *    be published in several academies at once (or in none, while it is a
 *    draft). There is no "the" organization of a course to put in an
 *    `eq()`.
 * 2. The organization was NEVER the write-authorization boundary here.
 *    That is the user's identity: `course.author_id` or a row in
 *    `course_editor` (see `can_author_course` +
 *    `child_key_belongs_to_course` in `packages/core-rust/src/db/sync.rs`,
 *    already hardened against IDOR — that function's comment documents the
 *    real attack it closed). Organization membership only grants
 *    VISIBILITY (which courses sync) and the permission to publish
 *    (`can_publish_in_org`, CC-40) — never to edit content. A scope by
 *    `organization_id` would not close the real authorization gap (which
 *    is already closed server-side, by user) and one by `author_id` would
 *    break legitimate saves by a `course_editor` who is not the author.
 * 3. `enrollment` and `lesson_progress` do not need a scope for another reason: they are
 *    PRIVATE per `user_id`, forced server-side
 *    (`WHERE id=$1 AND user_id=$2`) and filtered at source by the
 *    stream itself (`WHERE user_id = auth.user_id()`) — a row from another user
 *    NEVER reaches the local view, with or without organizations in between.
 *
 * In short: the column to narrow by is missing and, even if it existed, narrowing by
 * organization would not protect anything that user identity does not
 * already protect. Closing it for real would require the model to have a notion of "edit
 * as this organization" — today it does not.
 *
 * ⚠️ Signal that reopens this: if the product adds a "co-editor of
 * organization" role that edits course content published in their academy WITHOUT
 * being `author_id` nor appearing in `course_editor` — there, organization_id (via
 * `course_organization`) would become a real authorization boundary and
 * would need its own mechanism (it does not fit `options.scope`, which compares
 * columns of the row itself: it would require a subquery/JOIN).
 */

import {
  and,
  block,
  course,
  enrollment,
  eq,
  lesson,
  lesson_progress,
  type PowerSyncDrizzleDB,
  unit,
} from "@aula/powersync";
import type { BlockResponse } from "../learning-blocks/types";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  LocalWriteLostError,
  patchRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
} from "../powersync/verified-write";

// ─── Authorship: course ─────────────────────────────────────────────────────

export interface CreateCourseInput {
  title?: string;
  description?: string;
  category?: string;
  language?: string;
}

/** Creates a course in draft. The author is the current user (sync enforces authz). */
export async function createCourseMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: CreateCourseInput = {},
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(course).values({
    id,
    author_id: userId,
    title: input.title?.trim() || "Nuevo curso",
    description: input.description ?? null,
    category: input.category ?? null,
    language: input.language ?? "es",
    visibility: "draft",
    version: 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * UPDATE of the `course` row that **cannot be lost silently**.
 *
 * An `UPDATE … WHERE id = ?` against the PowerSync view can match **ZERO
 * rows and return "success"** (see why in `powersync/verified-write.ts`,
 * measured in the sister domain `space-academy-app`, `activity` of course
 * `06a131d0`: the title the author had just written never generated a CRUD op).
 * The author sees their text on screen, the database keeps
 * "Nuevo curso" forever, the course goes to the catalog with that name and every
 * certificate issued is sealed with it: an official document with the
 * wrong course name.
 *
 * Converged to the generic `patchRowVerified` of the canon (`listto-app@9ae347b6`,
 * `space-academy-app@16bc571f`) because the same window affects `unit`,
 * `lesson`, `block`, `enrollment` and `lesson_progress`. `updated_at` is set by
 * `patchRowVerified` on each attempt.
 */
export async function updateCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  updates: Partial<{
    title: string;
    description: string | null;
    category: string | null;
    language: string;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const patch = { ...updates } as Record<string, unknown>;
  if (typeof patch.title === "string") {
    patch.title = patch.title.trim() || "Nuevo curso";
  }
  await patchRowVerified(db, course, course.id, courseId, patch, options);
}

/** Publishes course to public catalog (guest-first). Seals `published_at`. */
export async function publishCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, course, course.id, courseId, { visibility: "public" }, options);
}

/** Returns the course to draft (withdraws it from the catalog). */
export async function unpublishCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, course, course.id, courseId, { visibility: "draft" }, options);
}

/**
 * ⚠️ DEAD: no caller anywhere in the repo (verified with
 * `git grep deleteCourseMutation` over `*.ts`/`*.tsx` — only this definition).
 * There is no UI to delete a course; the authoring editor only deletes
 * unit/lesson/block. It is deliberately left UNconverted to `patchRowVerified`
 * (lesson from `malla-vpn-app`: hardening dead mutations closes no real window and
 * gives a false sense of coverage). Report if a route that calls it is
 * reactivated — then it will need the protection.
 */
export async function deleteCourseMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
): Promise<void> {
  await db
    .update(course)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(course.id, courseId));
}

// ─── Authorship: unit / lesson / block ──────────────────────────────────────

export async function addUnitMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  opts: { title?: string; position?: number } = {},
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(unit).values({
    id,
    course_id: courseId,
    title: opts.title?.trim() || "Nueva unidad",
    position: opts.position ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateUnitMutation(
  db: PowerSyncDrizzleDB,
  unitId: string,
  updates: Partial<{ title: string; position: number }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, unit, unit.id, unitId, { ...updates }, options);
}

export async function deleteUnitMutation(
  db: PowerSyncDrizzleDB,
  unitId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, unit, unit.id, unitId, { deleted_at: nowISO() }, options);
}

export async function addLessonMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    unitId: string;
    title?: string;
    position?: number;
    xpReward?: number;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(lesson).values({
    id,
    unit_id: params.unitId,
    course_id: params.courseId,
    title: params.title?.trim() || "New lesson",
    position: params.position ?? 0,
    xp_reward: params.xpReward ?? 10,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateLessonMutation(
  db: PowerSyncDrizzleDB,
  lessonId: string,
  updates: Partial<{ title: string; position: number; xp_reward: number }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, lesson, lesson.id, lessonId, { ...updates }, options);
}

export async function deleteLessonMutation(
  db: PowerSyncDrizzleDB,
  lessonId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, lesson, lesson.id, lessonId, { deleted_at: nowISO() }, options);
}

export async function addBlockMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    lessonId: string;
    kind: string;
    content: unknown;
    position?: number;
    weight?: number;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(block).values({
    id,
    lesson_id: params.lessonId,
    course_id: params.courseId,
    kind: params.kind,
    position: params.position ?? 0,
    weight: params.weight ?? 1,
    content: JSON.stringify(params.content ?? {}),
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateBlockMutation(
  db: PowerSyncDrizzleDB,
  blockId: string,
  updates: Partial<{ kind: string; content: unknown; position: number; weight: number }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (updates.kind !== undefined) {
    patch.kind = updates.kind;
  }
  if (updates.position !== undefined) {
    patch.position = updates.position;
  }
  if (updates.weight !== undefined) {
    patch.weight = updates.weight;
  }
  if (updates.content !== undefined) {
    patch.content = JSON.stringify(updates.content);
  }
  await patchRowVerified(db, block, block.id, blockId, patch, options);
}

export async function deleteBlockMutation(
  db: PowerSyncDrizzleDB,
  blockId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, block, block.id, blockId, { deleted_at: nowISO() }, options);
}

// ─── Consumption: enrollment + progress ─────────────────────────────────────

/** Enrolls the user in a course (idempotent via the server's unique constraint). */
export async function enrollMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  courseId: string,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(enrollment).values({
    id,
    user_id: userId,
    course_id: courseId,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * ⚠️ DEAD: no caller in entire repo (verified with
 * `git grep unenrollMutation` — only this definition). There is no "unenroll"
 * button in course consumption. Like `deleteCourseMutation`: deliberately left
 * UNCONVERTED (lesson from `malla-vpn-app`).
 */
export async function unenrollMutation(
  db: PowerSyncDrizzleDB,
  enrollmentId: string,
): Promise<void> {
  await db
    .update(enrollment)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(enrollment.id, enrollmentId));
}

export interface SaveLessonProgressInput {
  progressId?: string;
  userId: string;
  courseId: string;
  lessonId: string;
  status: "in_progress" | "completed";
  lastBlockId?: string;
  /** Locally asserted score (0..1). The server re-verifies it; informative only. */
  claimedScore?: number;
  responses?: BlockResponse[];
  startedAt?: number;
  finishedAt?: number;
}

/** `waitForRowPresent` without throwing: `true`/`false` instead of `LocalWriteLostError`. */
async function rowEventuallyPresent(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<boolean> {
  try {
    await waitForRowPresent(db, lesson_progress, lesson_progress.id, id, options);
    return true;
  } catch (err) {
    if (err instanceof LocalWriteLostError) {
      return false;
    }
    throw err;
  }
}

/**
 * Persists lesson progress. Sends the `responses` (JSON string) and the
 * asserted score so the server RE-CORRECTS on the write-path and computes the authoritative
 * XP. Returns the `lesson_progress` row id (stable per lesson
 * for resume). The client can paint immediate feedback with
 * `applyLessonCompletion`, but the final grade/XP arrives via sync.
 *
 * `lesson_progress` is the student's progress: losing this write
 * silently does not lose a text field, it loses or DUPLICATES it. Two windows
 * with the same root cause (the view is rebuilt from the
 * checkpoint):
 *
 * 1. Known row (`existing`/`progressId`) and the UPDATE matches nothing → covered
 *    by `patchRowVerified`, like the rest of this module's tables.
 * 2. The alternate-key read (`user_id`+`lesson_id`) that resolves the id
 *    can return EMPTY while the row from a PRIOR save in this same
 *    session has not landed yet — it is not a student starting the lesson, it is
 *    the same window seen from a SELECT instead of an UPDATE (the failure
 *    class that a SELECT-then-decide `upsert` does not cover: it DUPLICATES the row
 *    instead of losing the data). Without verification, the `else` path below would
 *    generate a new id and leave the progress duplicated — two active
 *    rows for the same lesson, ambiguous progress. If the caller already has a
 *    `progressId` (the one returned by the previous save; `lesson-player.tsx`
 *    caches it for exactly that purpose), it waits (with backoff, like
 *    `waitForRowPresent`) for the row to reappear before assuming it does not
 *    exist.
 */
export async function saveLessonProgressMutation(
  db: PowerSyncDrizzleDB,
  input: SaveLessonProgressInput,
  options?: VerifiedWriteOptions,
): Promise<string> {
  const now = nowISO();
  // PowerSync exposes local tables as VIEWS and does NOT accept UPSERT
  // (`ON CONFLICT`) on them → an `.onConflictDoUpdate()` throws «cannot UPSERT
  // a view» and progress did NOT get written (neither local nor sync). We resolve the row
  // STABLE by (user_id, lesson_id) and do a manual UPDATE or INSERT. Thus there is
  // one row per lesson (resume by last_block_id) without depending on UPSERT.
  const patch = {
    user_id: input.userId,
    course_id: input.courseId,
    lesson_id: input.lessonId,
    status: input.status,
    last_block_id: input.lastBlockId ?? null,
    claimed_score: input.claimedScore ?? null,
    responses: input.responses ? JSON.stringify(input.responses) : null,
    started_at: input.startedAt ?? null,
    finished_at: input.finishedAt ?? null,
  };

  const existing = await db
    .select({ id: lesson_progress.id })
    .from(lesson_progress)
    .where(
      and(eq(lesson_progress.user_id, input.userId), eq(lesson_progress.lesson_id, input.lessonId)),
    )
    .limit(1);

  if (existing[0]) {
    await patchRowVerified(db, lesson_progress, lesson_progress.id, existing[0].id, patch, options);
    return existing[0].id;
  }

  if (input.progressId && (await rowEventuallyPresent(db, input.progressId, options))) {
    await patchRowVerified(
      db,
      lesson_progress,
      lesson_progress.id,
      input.progressId,
      patch,
      options,
    );
    return input.progressId;
  }

  const id = input.progressId ?? generateId();
  await db.insert(lesson_progress).values({ id, ...patch, updated_at: now, created_at: now });
  return id;
}
